function resolveX(v, area) {
  if (typeof v !== 'number') return area.x;
  return Math.abs(v) <= 1 ? area.x + v * area.w : v;
}

function resolveY(v, area) {
  if (typeof v !== 'number') return area.y;
  return Math.abs(v) <= 1 ? area.y + v * area.h : v;
}

function resolveW(v, area) {
  if (typeof v !== 'number') return 0;
  return Math.abs(v) <= 1 ? v * area.w : v;
}

function resolveH(v, area) {
  if (typeof v !== 'number') return 0;
  return Math.abs(v) <= 1 ? v * area.h : v;
}

function drawArrowHead(ctx, x1, y1, x2, y2, size = 8) {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const left = a + Math.PI * 0.84;
  const right = a - Math.PI * 0.84;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 + Math.cos(left) * size, y2 + Math.sin(left) * size);
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 + Math.cos(right) * size, y2 + Math.sin(right) * size);
  ctx.stroke();
}

function drawSpring(ctx, x1, y1, x2, y2, coils = 8, amplitude = 10) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;

  const lead = Math.min(14, len * 0.12);
  const tail = Math.min(14, len * 0.12);
  const innerLen = Math.max(0, len - lead - tail);
  const step = coils > 0 ? innerLen / coils : innerLen;

  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1 + ux * lead, y1 + uy * lead);

  for (let i = 0; i <= coils; i++) {
    const t = lead + i * step;
    const side = i % 2 === 0 ? -1 : 1;
    const sx = x1 + ux * t + px * amplitude * side;
    const sy = y1 + uy * t + py * amplitude * side;
    ctx.lineTo(sx, sy);
  }

  ctx.lineTo(x2 - ux * tail, y2 - uy * tail);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

export const primitive2dRenderer = {
  draw({ ctx, params }) {
    const area = params?.area;
    if (!area) return;

    const primitive = String(params.primitive || '').toLowerCase();
    const stroke = params.stroke || 'rgba(245,250,255,0.95)';
    const fill = params.fill || null;
    const lineWidth = Number.isFinite(params.lineWidth) ? Math.max(0.5, params.lineWidth) : 2;
    const alpha = Number.isFinite(params.alpha) ? Math.max(0, Math.min(1, params.alpha)) : 1;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = stroke;
    ctx.fillStyle = fill || stroke;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (primitive === 'circle') {
      const cx = resolveX(params.xk, area);
      const cy = resolveY(params.yk, area);
      const r =
        typeof params.rk === 'number'
          ? Math.abs(params.rk) * Math.min(area.w, area.h)
          : Number(params.r || 8);
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(1, r), 0, Math.PI * 2);
      if (fill) ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (primitive === 'line' || primitive === 'arrow') {
      const x1 = resolveX(params.x1k, area);
      const y1 = resolveY(params.y1k, area);
      const x2 = resolveX(params.x2k, area);
      const y2 = resolveY(params.y2k, area);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      if (primitive === 'arrow') {
        drawArrowHead(ctx, x1, y1, x2, y2, Number(params.arrowSize || 8));
      }
      ctx.restore();
      return;
    }

    if (primitive === 'rect') {
      const x = resolveX(params.xk, area);
      const y = resolveY(params.yk, area);
      const w = resolveW(params.wk, area);
      const h = resolveH(params.hk, area);
      if (fill) ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
      return;
    }

    if (primitive === 'polyline') {
      const points = Array.isArray(params.points) ? params.points : [];
      if (points.length < 2) {
        ctx.restore();
        return;
      }
      ctx.beginPath();
      points.forEach((p, i) => {
        const px = resolveX(p.xk, area);
        const py = resolveY(p.yk, area);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (primitive === 'spring') {
      const x1 = resolveX(params.x1k, area);
      const y1 = resolveY(params.y1k, area);
      const x2 = resolveX(params.x2k, area);
      const y2 = resolveY(params.y2k, area);
      drawSpring(
        ctx,
        x1,
        y1,
        x2,
        y2,
        Number(params.coils || 8),
        Number.isFinite(params.ampPx) ? params.ampPx : 10
      );
      ctx.restore();
      return;
    }

    if (primitive === 'pendulum') {
      const ax = resolveX(params.anchorXk, area);
      const ay = resolveY(params.anchorYk, area);
      const len =
        typeof params.lengthK === 'number'
          ? Math.abs(params.lengthK) * Math.min(area.w, area.h)
          : Number(params.lengthPx || 90);
      const angle = Number.isFinite(params.angleRad) ? params.angleRad : -Math.PI / 8;
      const bx = ax + Math.sin(angle) * len;
      const by = ay + Math.cos(angle) * len;

      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(bx, by, Number(params.bobRadiusPx || 10), 0, Math.PI * 2);
      if (fill) ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  },
};
